import { Button, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../api/adminApi";
import AgentInboxSettingsContext from "./AgentInboxSettingsContext";
import type { GeneralInboxSettingsForm } from "./inboxSettingsValidation";
import { validateGeneralInboxSettingsForm } from "./inboxSettingsValidation";

const updateInbox = withErrorHandling(adminApi.updateInbox);

const GeneralAgentInboxSettingsSection = () => {
  const { inbox, onInboxUpdated, hasEditPermissions } = useDefinedContext(AgentInboxSettingsContext);
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [form, setForm] = useState<GeneralInboxSettingsForm>({
    name: inbox.name,
    description: inbox.description,
    touchedFields: [],
  });

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);

    const [updatedInbox, updateError] = await updateInbox(inbox.id, {
      name: form.name.trim(),
      description: form.description.trim(),
    });

    setSaving(false);

    if (updateError) {
      logError(updateError, "[GeneralAgentInboxSettingsSection] updateInbox");
      sendNotificationError(updateError.message);
      return;
    }

    sendNotification("Agent inbox settings updated successfully");
    onInboxUpdated(updatedInbox);
  };

  const handleReset = () => {
    setForm({
      name: inbox.name,
      description: inbox.description,
      touchedFields: [],
    });
  };

  const validationResult = validateGeneralInboxSettingsForm(form);

  const isDirty = form.name.trim() !== inbox.name.trim() || form.description.trim() !== inbox.description.trim();

  return (
    <Stack spacing={2.5} sx={{ pt: 2.5 }}>
      <TextField
        fullWidth
        disabled={!hasEditPermissions}
        label="Name"
        value={form.name}
        onChange={(e) =>
          setForm({ ...form, name: e.target.value, touchedFields: distinct([...form.touchedFields, "name"]) })
        }
        error={form.touchedFields.includes("name") && !validationResult.name?.isValid}
        helperText={form.touchedFields.includes("name") ? validationResult.name?.error : undefined}
      />
      <TextField
        fullWidth
        disabled={!hasEditPermissions}
        label="Description"
        value={form.description}
        onChange={(e) =>
          setForm({
            ...form,
            description: e.target.value,
            touchedFields: distinct([...form.touchedFields, "description"]),
          })
        }
        error={form.touchedFields.includes("description") && !validationResult.description?.isValid}
        helperText={form.touchedFields.includes("description") ? validationResult.description?.error : undefined}
      />
      {hasEditPermissions && (
        <Stack spacing={1} direction="row" sx={{ alignItems: "center", mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            loading={saving}
            disabled={!isDirty || !validationResult.isValid}
          >
            Save
          </Button>
          <Button variant="text" color="secondary" onClick={handleReset} disabled={!isDirty}>
            Cancel
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default GeneralAgentInboxSettingsSection;
